import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Inventory2,
  Inventory,
  Warning,
  Business,
  Phone,
  Email,
  Visibility,
  LocalOffer,
} from "@mui/icons-material";
import  {itemService}  from "../../services/itemService";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const ItemDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [itemBatches, setItemBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    fetchItemDetails();
  }, [id]);

  const fetchItemDetails = async () => {
    try {
      // Fetch item details
      const itemResponse = await itemService.getItem(id);
      const itemData = itemResponse.data.data.item || itemResponse.data.data;
      setItem(itemData);

      // Fetch item stock details
      try {
        const stockResponse = await itemService.getItemStock(id);
        const stockData = stockResponse.data.data;
        setItemBatches(stockData.batches || []);
      } catch (error) {
        console.warn("Using empty batches array");
        setItemBatches([]);
      }

      // Fetch suppliers for this item
      try {
        const { suppliersAPI } = await import("../../services/API");
        const suppliersResponse = await suppliersAPI.getSuppliersByItem(id);
        setSuppliers(suppliersResponse.data.data.suppliers || []);
      } catch (error) {
        console.warn("No suppliers found for this item");
        setSuppliers([]);
      }
    } catch (error) {
      console.error("Error fetching item details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getItemTypeColor = (itemType) => {
    const colors = {
      consumable: "primary",
      equipment: "secondary",
      raw_material: "info",
      finished_good: "success",
      other: "default",
    };
    return colors[itemType] || "default";
  };

  const getExpiryStatus = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return { status: "Expired", color: "error" };
    if (daysUntilExpiry <= 30)
      return { status: "Expiring Soon", color: "warning" };
    return { status: "Valid", color: "success" };
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const getStockStatus = (stockQty, minStock) => {
    if (stockQty === 0) return { label: "Out of Stock", color: "error" };
    if (stockQty <= minStock) return { label: "Low Stock", color: "warning" };
    return { label: "In Stock", color: "success" };
  };

  // Calculate total stock from all batches
  const totalStock = itemBatches.reduce(
    (sum, batch) => sum + (batch.stockQty || batch.availableQuantity || 0),
    0
  );

  // Get supplier product details for a specific item
  // const getSupplierProduct = (supplier, itemId) => {
  //   return (
  //     supplier.items?.find(
  //       (itm) => itm.item && itm.item._id === itemId
  //     ) ||
  //     supplier.supplierItems?.find(
  //       (itm) => itm.item && itm.item._id === itemId
  //     )
  //   );
  // };
  const getSupplierProduct = (supplier, itemId) => {
  // Backend returns supplierProduct directly, not in an array
  return supplier.supplierProduct || null;
};

  if (loading) {
    return <LinearProgress />;
  }

  if (!item) {
    return <Alert severity="error">Item not found</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/hospital/hospital-inventory/items")}
          variant="outlined"
        >
          Back to Items
        </Button>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {item.name}
        </Typography>
        {hasAnyRole(["admin", "inventory_manager"]) && (
          <Button
            startIcon={<Edit />}
            variant="contained"
            onClick={() => navigate(`/hospital/hospital-inventory/items/${id}/edit`)}
          >
            Edit Item
          </Button>
        )}
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Batches & Stock" />
        <Tab label={`Suppliers (${suppliers.length})`} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Basic Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Brand
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {item.brand}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Manufacturer
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {item.manufacturer}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Item Type
                    </Typography>
                    <Chip
                      label={item.itemType.replace("_", " ").toUpperCase()}
                      size="small"
                      color={getItemTypeColor(item.itemType)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Unit of Measurement
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {item.unit}
                    </Typography>
                  </Grid>
                  {item.category && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Category
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item.category}
                      </Typography>
                    </Grid>
                  )}
                  {item.barcode && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Barcode
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item.barcode}
                      </Typography>
                    </Grid>
                  )}
                  {item.hsnCode && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        HSN Code
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item.hsnCode}
                      </Typography>
                    </Grid>
                  )}
                  {item.description && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {item.description}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Storage Conditions */}
                {item.storageConditions && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Storage Conditions
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Temperature
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {item.storageConditions.temperature}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Humidity
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {item.storageConditions.humidity}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Light Sensitive
                        </Typography>
                        <Chip
                          label={item.storageConditions.lightSensitive ? "Yes" : "No"}
                          size="small"
                          color={item.storageConditions.lightSensitive ? "warning" : "default"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Refrigerated
                        </Typography>
                        <Chip
                          label={item.storageConditions.refrigerated ? "Yes" : "No"}
                          size="small"
                          color={item.storageConditions.refrigerated ? "info" : "default"}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Stock & Suppliers Summary */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Inventory color="primary" />
                  Stock Summary
                </Typography>

                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {totalStock}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Units in Stock ({item.unit})
                  </Typography>

                  <Box sx={{ mt: 3 }}>
                    <Chip
                      label={
                        getStockStatus(totalStock, item.minStock || 10)
                          .label
                      }
                      color={
                        getStockStatus(totalStock, item.minStock || 10)
                          .color
                      }
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {itemBatches.length} active batches
                    </Typography>
                    {item.minStock && (
                      <Typography variant="body2" color="text.secondary">
                        Min Stock: {item.minStock}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {totalStock <= (item.minStock || 10) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Stock is below minimum threshold. Consider placing a
                    purchase order.
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Available Suppliers Summary */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Business color="primary" />
                  Available Suppliers
                </Typography>

                {suppliers.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No suppliers found for this item
                  </Typography>
                ) : (
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color="primary.main"
                      sx={{ textAlign: "center", py: 1 }}
                    >
                      {suppliers.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", mb: 2 }}
                    >
                      Supplier{suppliers.length !== 1 ? "s" : ""} Available
                    </Typography>

                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      {suppliers.slice(0, 3).map((supplier) => {
                        const supplierProduct = getSupplierProduct(
                          supplier,
                          id
                        );
                        return (
                          <Box
                            key={supplier._id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1,
                              mb: 1,
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "primary.main",
                                fontSize: "0.875rem",
                              }}
                            >
                              {getInitials(supplier.name)}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {supplier.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {/* {supplierProduct
                                  ? `₹${
                                      supplierProduct.costPrice ||
                                      supplierProduct.purchaseRate
                                    }`
                                  : "Contact for price"} */}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {suppliers.length > 3 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: "center", display: "block", mt: 1 }}
                      >
                        +{suppliers.length - 3} more suppliers
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Batches & Stock Tab Content */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Batch Details & Stock Information
                </Typography>
                {itemBatches.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    No batches found for this item
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Batch Number</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Stock Quantity</TableCell>
                          <TableCell>Purchase Rate</TableCell>
                          <TableCell>MRP</TableCell>
                          <TableCell>Expiry Date</TableCell>
                          <TableCell>Stock Status</TableCell>
                          <TableCell>Expiry Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemBatches.map((batch) => {
                          const expiryStatus = getExpiryStatus(
                            batch.expiryDate
                          );
                          const stockStatus = getStockStatus(
                            batch.stockQty || batch.availableQuantity,
                            item.minStock || 10
                          );

                          return (
                            <TableRow key={batch._id || batch.batchNumber}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {batch.batchNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {batch.supplier ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {getInitials(batch.supplier.name)}
                                    </Avatar>
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {batch.supplier.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {batch.supplier.contactPerson}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No supplier
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {batch.stockQty || batch.availableQuantity} {item.unit}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  ₹{batch.costPrice || batch.purchaseRate}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  ₹{batch.sellingPrice || batch.mrp}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {new Date(
                                  batch.expiryDate
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={stockStatus.label}
                                  size="small"
                                  color={stockStatus.color}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={expiryStatus.status}
                                  size="small"
                                  color={expiryStatus.color}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Suppliers Tab Content */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Available Suppliers ({suppliers.length})
            </Typography>
            {suppliers.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 4 }}
              >
                No suppliers found for this item
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {suppliers.map((supplier) => {
                  const supplierProduct = getSupplierProduct(supplier, id);

                  return (
                    <Grid item xs={12} md={6} lg={4} key={supplier._id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          transition: "0.3s",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "primary.main",
                                width: 56,
                                height: 56,
                              }}
                            >
                              {getInitials(supplier.name)}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6" fontWeight="bold">
                                {supplier.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {supplier.contactPerson}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Phone fontSize="small" color="action" />
                              <Typography variant="body2">
                                {supplier.phone}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Email fontSize="small" color="action" />
                              <Typography variant="body2">
                                {supplier.email}
                              </Typography>
                            </Box>
                            {supplier.address && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1, fontSize: "0.875rem" }}
                              >
                                {typeof supplier.address === "string"
                                  ? supplier.address
                                  : `${supplier.address.street || ""} ${
                                      supplier.address.city || ""
                                    } ${supplier.address.state || ""} ${
                                      supplier.address.zipCode || ""
                                    }`.trim()}
                              </Typography>
                            )}
                          </Box>

                          {/* Supplier-specific item pricing */}
                          {supplierProduct && (
                            <Box
                              sx={{
                                mb: 2,
                                p: 2,
                                bgcolor: "background.default",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <LocalOffer fontSize="small" />
                                Pricing & Terms
                              </Typography>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Purchase Rate
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {/* ₹
                                    {supplierProduct.costPrice ||
                                      supplierProduct.purchaseRate} */}
                                      ₹{supplierProduct.costPrice}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    MRP
                                  </Typography>
                                  <Typography variant="body2">
                                    {/* ₹{supplierProduct.mrp} */}
                                    ₹{supplierProduct.sellingPrice || supplierProduct.mrp || 'N/A'}
                                  </Typography>
                                </Grid>
                                {supplierProduct.packSize && (
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Pack Size
                                    </Typography>
                                    <Typography variant="body2">
                                      {supplierProduct.packSize}
                                    </Typography>
                                  </Grid>
                                )}
                                {supplierProduct.discountPercent && (
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Discount
                                    </Typography>
                                    <Typography variant="body2">
                                      {supplierProduct.discountPercent}%
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          )}

                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                              mb: 2,
                            }}
                          >
                            {supplier.gstNumber && (
                              <Chip
                                label={`GST: ${supplier.gstNumber}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {supplier.panNumber && (
                              <Chip
                                label={`PAN: ${supplier.panNumber}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            <Chip
                              label={supplier.isActive ? "Active" : "Inactive"}
                              size="small"
                              color={supplier.isActive ? "success" : "default"}
                            />
                          </Box>

                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() =>
                              navigate(`/hospital/hospital-inventory/suppliers/${supplier._id}`)
                            }
                          >
                            View Supplier Details
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};
